/**
 * @module PaneContext
 * @description Context for managing pane navigation within a carousel/slider component
 */

import React, {
  createContext, useMemo, useCallback, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';

/**
 * @constant {React.Context} PaneContext
 * @description Context for sharing pane navigation functionality across components
 * @default undefined
 */
export const PaneContext = createContext();

const propTypes = {
  children: PropTypes.node.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  carouselSettings: PropTypes.object.isRequired,
};

/**
 * @function PaneContextProvider
 * @description Provider component that makes pane navigation controls available to child components
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render within the slider
 * @param {Object} props.carouselSettings - Settings for the react-slick carousel component
 * @param {React.Ref} ref - Forwarded ref to access the slider instance
 * @returns {React.Element} Provider component with pane navigation context wrapped around a Slider
 *
 * @example
 * const sliderRef = useRef(null);
 *
 * <PaneContextProvider
 *   ref={sliderRef}
 *   carouselSettings={{
 *     dots: true,
 *     infinite: false,
 *     speed: 500,
 *     slidesToShow: 1
 *   }}
 * >
 *   <FirstPane />
 *   <SecondPane />
 * </PaneContextProvider>
 */
const PaneContextProvider = forwardRef(({ children, carouselSettings }, sliderRef) => {
  /**
   * @function goToPane
   * @description Navigate to a specific pane by index using react-slick's API
   * @param {number} paneIndex - Index of the pane to navigate to
   * @returns {void}
   */
  const goToPane = useCallback((paneIndex) => {
    sliderRef.current.slickGoTo(paneIndex);
  }, [sliderRef]);

  const paneContextValue = useMemo(() => ({ goToPane }), [goToPane]);

  return (
    <PaneContext.Provider value={paneContextValue}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Slider ref={sliderRef} {...carouselSettings}>
        {children}
      </Slider>
    </PaneContext.Provider>
  );
});

PaneContextProvider.propTypes = propTypes;
export default PaneContextProvider;
